import type { QueryKey } from "@tanstack/react-query"
import { type ClassValue, clsx } from "clsx"
import type { FieldValues, Path, UseFormSetError } from "react-hook-form"
import { twMerge } from "tailwind-merge"
import { currLang, ELang } from "@/shared/config/locales.ts"
import queryClient from "@/shared/config/query-client.ts"
import type { DateRange, IOption, Translation } from "@/shared/types/index.ts"
// import { currLang } from "./i18n.ts"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

const getDay = (day: number) => (day === 0 ? 7 : day)
const isSameDate = (d1: Date, d2: Date) => d1.getDate() === d2.getDate() && d1.getMonth() === d2.getMonth() && d1.getFullYear() === d2.getFullYear()

export const getMonthName = (date?: Date) => {
  if (!date) return ""
  const months = {
    uz: ["yanvar", "fevral", "mart", "aprel", "may", "iyun", "iyul", "avgust", "sentyabr", "oktyabr", "noyabr", "dekabr"],
    uzKr: ["январ", "феврал", "март", "апрел", "май", "июн", "июл", "август", "сентябр", "октябр", "ноябр", "декабр"],
    ru: ["январь", "февраль", "март", "апрель", "май", "июнь", "июль", "август", "сентябрь", "октябрь", "ноябрь", "декабрь"]
  }
  const selectedMonths = months[currLang]
  return selectedMonths?.[date.getMonth()]
}

export const getRangeClasses = (date?: Date, range?: Date | DateRange) => {
  if (!Array.isArray(range)) return date?.DMY() === range?.DMY() ? "calendar__selected" : ""
  if (date?.DMY() === range?.[0]?.DMY()) return "calendar__pointStart"
  if (date?.DMY() === range?.[1]?.DMY()) return "calendar__pointEnd"
  if (range?.every(date => date != null) && date != null && date > range[0] && date < range[1]) return "calendar__between"
  return null
}

export function getCalendarDates(date: Date) {
  const now = new Date()
  const year = date.getFullYear()
  const month = date.getMonth()
  const dates: { isOut?: boolean; date: Date; isToday?: boolean; tasksCount?: number }[] = []
  const firstDayOfMonth = new Date(year, month, 1)
  const lastDayOfMonth = new Date(year, month + 1, 0)
  const daysInMonth = lastDayOfMonth.getDate()
  const startDay = getDay(firstDayOfMonth.getDay()) - 1
  if (startDay !== 0) {
    const prevMonthLastDay = new Date(year, month, 0).getDate()
    for (let i = startDay; i > 0; i--) {
      dates.push({ isOut: true, date: new Date(year, month - 1, prevMonthLastDay - i + 1) })
    }
  }
  for (let i = 1; i <= daysInMonth; i++) {
    const currentDate = new Date(year, month, i)
    dates.push({
      date: currentDate,
      isToday: isSameDate(now, currentDate)
    })
  }
  const endDay = getDay(lastDayOfMonth.getDay()) - 1
  if (endDay !== 6) {
    for (let i = 1; i <= 6 - endDay; i++) {
      dates.push({ date: new Date(year, month + 1, i), isOut: true })
    }
  }
  return dates
}

export const pack = (number: number) => number.toString().padStart(2, "0")

const BASE_URL = import.meta.env.VITE_BASE_URL as string

export const download = (href: string, download?: string, isBlank?: boolean, wihtoutPrefix?: boolean) => {
  const prefixPath = wihtoutPrefix ? "" : "/doc/filemanager/download/"
  const link = BASE_URL + prefixPath + href
  const anchor = document.createElement("a")
  anchor.href = link
  if (download != null) anchor.download = download
  anchor.click()
}

export const getFileFormat = (filename: string) => {
  const match = filename.match(/\.(\w+)$/)
  return match ? match[1] : null
}

export const formatFileSize = (bytes: number): string => {
  const units = ["Bytes", "KB", "MB", "GB", "TB"]
  let i = Math.floor(Math.log(bytes) / Math.log(1024))
  i = Math.min(i, units.length - 1)
  const size = bytes / Math.pow(1024, i)
  return `${size.toFixed(2)} ${units[i]}`
}

export const validateDays = (value: string) => {
  const regex = /^([\d]{1,2}|-[\d]{1,2}|[\d]{1,2}-|[\d]{1,2}-[\d]{1,2})(,([\d]{1,2}|-[\d]{1,2}|[\d]{1,2}-|[\d]{1,2}-[\d]{1,2}))*$/
  return regex.test(value)
}

export const mergeDateAndTime = (dateString: string, timeString: string): Date => {
  const date = new Date(dateString)
  const [hours, minutes] = timeString.split(":").map(Number)
  date.setHours(hours, minutes, 0, 0)
  return date
}

export const formatTime = (input: string | Date): string => {
  let date: Date
  if (input instanceof Date) {
    date = input
  } else {
    if (input === "") return "--:--"
    date = new Date(input)
  }
  const hours = date.getHours().toString().padStart(2, "0")
  const minutes = date.getMinutes().toString().padStart(2, "0")
  return `${hours}:${minutes}`
}

export const playNotificationSound = () => {
  const audio = new Audio("/assets/notification.mp3")
  audio.play()
}

export const getChatPageUserId = () => {
  const id = window.location.pathname.match(/\/chat\/(\d+)\/?$/)?.[1]
  if (id) return Number(id)
}

export function centerTextWithEllipses(text: string, maxLength = 30) {
  if (text.length <= maxLength) {
    return text
  }
  const charsToKeep = Math.floor((maxLength - 3) / 2)
  const start = text.slice(0, charsToKeep)
  const end = text.slice(-charsToKeep)

  return `${start}...${end}`
}

export class TypeCheck {
  static isString(value: unknown): value is string {
    return typeof value === "string"
  }

  static isNumber(value: unknown): value is number {
    return typeof value === "number"
  }

  static isBoolean(value: unknown): value is boolean {
    return typeof value === "boolean"
  }

  static isObject(value: unknown): value is object {
    return typeof value === "object" && value !== null
  }

  static isArray(value: unknown): value is unknown[] {
    return Array.isArray(value)
  }

  static isFunction(value: unknown): value is void {
    return typeof value === "function"
  }

  static isNull(value: unknown): value is null {
    return value === null
  }

  static isUndefined(value: unknown): value is undefined {
    return value === undefined
  }

  static isSymbol(value: unknown): value is symbol {
    return typeof value === "symbol"
  }
}

export const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message
  }
  if (typeof error === "object" && error !== null && "data" in error) {
    const customError = error as { data?: { message?: string } }
    return customError.data?.message ?? "something_went_wrong"
  }
  return "something_went_wrong"
}

export const toCamelCase = (str: string): string => {
  return str.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase())
}

export const findOption = <T extends string | number, M extends object>(arr: IOption<T, M>[], val: T): IOption<T, M> | undefined => {
  for (const option of arr) {
    if (option.value === val) {
      return option
    }
    if (option.children) {
      const foundChild = findOption(option.children, val)
      if (foundChild) {
        return foundChild
      }
    }
  }
  return undefined
}

export function getLastPath(path: string): string {
  return path.replace(/\/+$/, "").split("/").pop() ?? ""
}

export function toConstantCase(str: string) {
  return str
    .replace(/^\/|\/$/g, "")
    .replace(/\//g, "_")
    .toUpperCase()
}

export const getRoleServices = (): string[] => import.meta.env.VITE_ROLE_SERVICES.split(",")

export const invalidate = (queryKey: string | QueryKey) => {
  queryClient.invalidateQueries(
    typeof queryKey === "string"
      ? {
          predicate: query => query.queryKey[0] === queryKey
        }
      : { queryKey }
  )
}

export const isArrayOfObjects = (value: unknown): value is Record<string, unknown>[] => {
  return Array.isArray(value) && value.every(item => typeof item === "object" && item !== null)
}

export const isArrayOfStrings = (value: unknown): value is string[] => {
  return Array.isArray(value) && value.every(item => typeof item === "string")
}

export const setValidationErrors = <T extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<T>,
  fieldMapping?: Record<string, Path<T>>
): void => {
  if (typeof error === "object" && error !== null && "data" in error) {
    const customError = error as {
      data: Record<string, unknown>
    }
    const details = customError.data
    if (details && typeof details === "object") {
      for (const [backendField, messages] of Object.entries(details)) {
        if (isArrayOfObjects(messages)) {
          messages.forEach((nestedError, index) => {
            if (typeof nestedError === "object" && nestedError !== null) {
              for (const [nestedField, nestedMessages] of Object.entries(nestedError)) {
                const nestedFormField = `${backendField}[${index}].${nestedField}`
                const translatedMessages = isArrayOfStrings(nestedMessages)
                  ? (nestedMessages as string[])?.map(message => message) //add translate here
                  : Object.values(nestedMessages as Record<number, string[]>)
                      ?.flat()
                      ?.map(message => message) //add translate here
                setError(nestedFormField as Path<T>, {
                  type: "server",
                  message: translatedMessages.join("\n")
                })
              }
            }
          })
        } else {
          const formField = fieldMapping?.[backendField] || backendField
          const translatedMessages = (messages as string[]).map(message => message) //here also
          setError(formField as Path<T>, {
            type: "server",
            message: translatedMessages.join("\n")
          })
        }
      }
    }
  }
}

export const getOptionValue = <T>(data: T) => {
  return TypeCheck.isObject(data) && "value" in data ? (data as { value: T }).value : data
}

export const tl = (value: { translations: Translation }, key = "name") => {
  const tlVal = value.translations?.[currLang]?.[key]
  if (!tlVal && currLang === ELang.RU) {
    return "Нет перевода"
  } else if (!tlVal && currLang === ELang.UZ) {
    return "Tarjimasi yo‘q"
  } else if (!tlVal && currLang === ELang.UZ_CR) {
    return "Таржимаси йўқ"
  }
  return value.translations?.[currLang]?.[key] ?? "Нет перевода"
}

export const buildUrl = (url: string, id?: string | number) => `${url}/${id ? `${id}/` : ""}`

export const cvToConstant = (url: string): string => {
  return url
    .toUpperCase()
    .replace(/^\/|\/$/g, "")
    .replace(/-/g, "_")
    .replace(/\//g, "_")
}

export const abbreviateName = (fullName = "-"): string => {
  const parts = fullName.trim().split(" ")
  if (parts.length < 2) return fullName
  const [lastName, firstName, patronymic] = parts
  const firstInitial = firstName ? `${firstName[0]}.` : ""
  const patronymicInitial = patronymic ? `${patronymic[0]}.` : ""
  return `${lastName} ${firstInitial} ${patronymicInitial}`.trim()
}

export const formatDate = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${day}.${month}.${year}`
}
